/**
 * Transport-failure replay policy for the shared API client.
 *
 * A request that fails with no readable HTTP response may have died in
 * transit (or been hidden by the browser), so an idempotent read is safe to
 * replay once. JAVASCRIPT-REACT-3E: an iOS score-breakdown GET vanished
 * between the phone and Render's edge while the same device's next requests
 * succeeded seconds later.
 */
export const NETWORK_RETRY_DELAY_MS = 500;

const IDEMPOTENT_METHODS = new Set(['get', 'head', 'options']);

interface RetryAbortSignal {
  readonly aborted: boolean;
  addEventListener?: (
    type: 'abort',
    listener: () => void,
    options?: { once?: boolean },
  ) => void;
  removeEventListener?: (type: 'abort', listener: () => void) => void;
}

export interface NetworkRetryConfig {
  method?: string;
  url?: string;
  signal?: RetryAbortSignal;
  networkRetried?: boolean;
}

export interface RetryableNetworkError {
  isAxiosError: true;
  code?: string;
  response?: unknown;
  config: NetworkRetryConfig;
}

export interface NetworkRetryBreadcrumb {
  category: 'api.network_retry';
  message: string;
  level: 'warning';
  data: {
    method: string;
    requestPath?: string;
    errorCode?: string;
    delayMs: number;
  };
}

const requestPathWithoutQuery = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  const withoutQuery = url.split(/[?#]/, 1)[0];
  if (!withoutQuery) return undefined;
  try {
    return /^https?:\/\//i.test(withoutQuery)
      ? new URL(withoutQuery).pathname
      : withoutQuery;
  } catch {
    return withoutQuery;
  }
};

/** Build privacy-safe retry telemetry without headers, tokens, or query data. */
export const buildNetworkRetryBreadcrumb = (
  error: RetryableNetworkError,
): NetworkRetryBreadcrumb => ({
  category: 'api.network_retry',
  message: 'Retrying an idempotent API read after a transport failure',
  level: 'warning',
  data: {
    method: (error.config.method ?? 'get').toUpperCase(),
    requestPath: requestPathWithoutQuery(error.config.url),
    errorCode: error.code,
    delayMs: NETWORK_RETRY_DELAY_MS,
  },
});

export const shouldRetryNetworkError = (
  error: unknown,
): error is RetryableNetworkError => {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as Partial<RetryableNetworkError>;
  // ERR_NETWORK with no response is a transport failure. Timeouts
  // (ECONNABORTED), cancellations (ERR_CANCELED) and every HTTP status are
  // real outcomes and pass through untouched.
  if (candidate.isAxiosError !== true || candidate.code !== 'ERR_NETWORK') return false;
  if (candidate.response) return false;
  const config = candidate.config;
  if (!config || config.networkRetried || config.signal?.aborted) return false;
  return IDEMPOTENT_METHODS.has((config.method ?? 'get').toLowerCase());
};
